import React, {FC} from 'react'
import {Grid, Tabs, TabsAlignment, TabsSkin, TabsVariant, Text} from 'wix-ui-tpa/cssVars'
import {useEnvironment, useTranslation, WidgetProps} from '@wix/yoshi-flow-editor'
import {useSettings, useStyles} from '@wix/tpa-settings/react'

import {stylesParams} from '../stylesParams'
import {settingsParams} from '../settingsParams'
import {LAYOUT_BREAKPOINTS_START} from '../constants'
import {withExperiencesAddOnStorageProvider} from '../storage'
import {
  useGetLayoutSize,
  withLayoutSizeProvider,
} from '../../../components-shared/LayoutSizeProvider'
import {Alignment} from '../../../types/createStylesParams'
import {LayoutSize} from '../../../components-shared/LayoutSizeProvider/types'

import {useHooks} from './useHooks'
import {experiencesAddOnDataHooks} from './constants'
import {classes, st} from './Widget.st.css'
import {Experience} from '../../ExperiencesList/Widget/components/Experience'
import {EmptyState} from '../../ExperiencesList/Widget/components/EmptyState'

const Widget: FC<WidgetProps> = () => {
  const {isMobile} = useEnvironment()
  const {t} = useTranslation()
  const {layoutSize} = useGetLayoutSize()
  const settings = useSettings()
  const styles = useStyles()

  const titleText: string = settings.get(settingsParams.titleText)
  const allReservationLocationsTabText: string = settings.get(settingsParams.allLocationsTabText)

  const {
    regionalSettings,
    selectedReservationLocationTabIndex,
    reservationLocationTabOptions,
    selectedExperiences,
    showErrorState,
    showEmptyState,
    handleReservationLocationSelect,
    handleGoToExperience,
  } = useHooks({allReservationLocationsTabText})

  const showTitle: boolean = settings.get(settingsParams.showTitle)
  const showLocationTabs: boolean = settings.get(settingsParams.showLocationTabs)

  const locationTabsAlignment: Alignment = styles.get(stylesParams.locationTabsAlignment)
  const locationTabsAlignmentProp: TabsAlignment =
    ReservationLocationsTabsAlignment[locationTabsAlignment]
  const fitLocationTabsWidthToListWidth: boolean = isMobile
    ? true
    : styles.get(stylesParams.fitLocationTabsWidthToListWidth) === 1

  const maximumExperiencesPerRow: number = styles.get(stylesParams.maximumExperiencesPerRow)
  const spaceBetweenExperiences: number = styles.get(stylesParams.spaceBetweenExperiences)

  const showReservationLocationTabsOverride = showLocationTabs
    ? reservationLocationTabOptions.length > 2
    : false

  const desktopMaximumExperiencesGridColumns =
    selectedExperiences.length >= maximumExperiencesPerRow
      ? maximumExperiencesPerRow
      : selectedExperiences.length
  const maximumExperiencesGridColumns =
    isMobile && typeof MaximumExperiencesGridColumnsForMobileLayout[layoutSize] !== 'undefined'
      ? MaximumExperiencesGridColumnsForMobileLayout[layoutSize]
      : desktopMaximumExperiencesGridColumns

  const showExperienceCoverImages = selectedExperiences.some(
    ({configuration}) => !!configuration?.displayInfo?.coverImage?.id,
  )

  const renderExperiences = () =>
    showErrorState ? (
      <div className={st(classes.errorState)}>
        <Text>{t('uou-reservations.experiences-addon.error-state')}</Text>
      </div>
    ) : showEmptyState ? (
      <EmptyState />
    ) : (
      <Grid
        className={st(classes.experiencesGrid)}
        maxColumns={maximumExperiencesGridColumns}
        rowGap={spaceBetweenExperiences}
        columnGap={spaceBetweenExperiences}
      >
        {selectedExperiences.map((experience) => (
          <Grid.Item key={experience.id} colSpan={1} rowSpan={1}>
            <Experience
              regionalSettings={regionalSettings}
              experience={experience}
              showCoverImages={showExperienceCoverImages}
              handleGoToExperience={handleGoToExperience}
            />
          </Grid.Item>
        ))}
      </Grid>
    )

  return (
    <div
      className={st(classes.root, {[layoutSize]: true})}
      data-hook={experiencesAddOnDataHooks.root()}
    >
      {showTitle ? (
        <Text tagName="h2" className={st(classes.title)}>
          {titleText}
        </Text>
      ) : null}

      {showReservationLocationTabsOverride ? (
        <Tabs
          className={st(classes.reservationLocationsTabs)}
          activeTabIndex={selectedReservationLocationTabIndex}
          items={reservationLocationTabOptions}
          onTabClick={handleReservationLocationSelect}
          skin={TabsSkin.fitUnderline}
          alignment={locationTabsAlignmentProp}
          variant={fitLocationTabsWidthToListWidth ? TabsVariant.fullWidth : TabsVariant.fit}
          isTablist
          aria-label={titleText}
        />
      ) : null}

      {showReservationLocationTabsOverride
        ? reservationLocationTabOptions.map(({id}, index) => {
            const tabId = id!

            return (
              <div
                key={tabId}
                id={`${tabId}-tab`}
                role="tabpanel"
                aria-labelledby={tabId}
                hidden={index !== selectedReservationLocationTabIndex}
              >
                {index === selectedReservationLocationTabIndex ? renderExperiences() : null}
              </div>
            )
          })
        : renderExperiences()}
    </div>
  )
}

export default withLayoutSizeProvider(
  withExperiencesAddOnStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
)

const ReservationLocationsTabsAlignment: Record<
  Exclude<Alignment, Alignment.Stretch>,
  TabsAlignment
> = {
  [Alignment.Left]: TabsAlignment.left,
  [Alignment.Center]: TabsAlignment.center,
  [Alignment.Right]: TabsAlignment.right,
}

const MaximumExperiencesGridColumnsForMobileLayout: Record<LayoutSize.XS | LayoutSize.S, number> = {
  [LayoutSize.XS]: 1,
  [LayoutSize.S]: 2,
}
